import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import moment from "moment";

import { useFeeds } from "../../../../../reactQuery/feeds";
import { getResponseData, getTotalRecord } from "../../../../../reactQuery/response";
import { parseDateRangeSearchParams } from "../../../../../utils";

function parseDateFilters(searchParams) {
  const parsedDate = parseDateRangeSearchParams({
    from: searchParams.get("from"),
    days: searchParams.get("days"),
    to: searchParams.get("to"),
    date: searchParams.get("date"),
  });

  if (!parsedDate) {
    return { start_date: null, end_date: null };
  }

  const formatDate = (dateString) => {
    if (!dateString) {
      return null;
    }

    return moment(dateString).format("YYYY-MM-DD[T]00:00:00");
  };

  return {
    start_date: formatDate(parsedDate.startDate),
    end_date: formatDate(parsedDate.endDate),
  };
}

function parseUserIds(searchParams) {
  const searchParam = searchParams.get("search");

  if (!searchParam) {
    return null;
  }

  return searchParam
    .split(",")
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !Number.isNaN(id));
}

function mapFeedItem(item) {
  if (
    item.history_type === "Comment" &&
    [
      "create_comment",
      "update_comment",
      "delete_comment",
    ].includes(item.response_object?.event_type)
  ) {
    return {
      ...item,
      content: "comment_content",
      id: item.history_id,
    };
  }

  if (
    item.history_type === "Card" &&
    item.response_object?.event_type === "card_log_created"
  ) {
    const newKey = item.response_object?.new_key || {};

    return {
      ...item,
      content: "Worklog_content",
      id: item.history_id,
      card_id: item.card_id,
      work_performed: newKey.work_performed,
      hour_spent: newKey.hours_spent,
      allocated_hours: newKey.allocated_hours,
      workflow_step: newKey.work_log_step,
      log_type: newKey.log_type,
      created_at: item.at_time,
      username: item.response_object?.user_object?.username,
      user_image: item.response_object?.user_object?.user_image,
      user_profile_color: item.response_object?.user_object?.profile_color,
    };
  }

  return {
    ...item,
    content: "history_content",
    id: item.history_id,
  };
}

export function useFeedHistoryItems(boardId) {
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [allData, setAllData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 100;
  const cardId = searchParams.get("card_id");

  const dateFilters = useMemo(() => parseDateFilters(searchParams), [searchParams]);
  const userIds = useMemo(() => parseUserIds(searchParams), [searchParams]);
  const filters = useMemo(
    () => ({
      milestoneId: searchParams.get("milestone"),
      user_ids: userIds,
      start_date: dateFilters.start_date,
      end_date: dateFilters.end_date,
    }),
    [dateFilters.end_date, dateFilters.start_date, searchParams, userIds]
  );

  const { feeds, isLoading, isFetching } = useFeeds(
    boardId,
    currentPage,
    itemsPerPage,
    filters
  );

  const feedItems = useMemo(() => getResponseData(feeds, []).map(mapFeedItem), [feeds]);
  const totalRecord = useMemo(() => getTotalRecord(feeds), [feeds]);

  useEffect(() => {
    setCurrentPage(1);
    setAllData([]);
    setHasMore(true);
  }, [filters.milestoneId, filters.start_date, filters.end_date, filters.user_ids]);

  useEffect(() => {
    if (currentPage === 1) {
      setAllData(feedItems);
    } else if (feedItems.length) {
      setAllData((previousItems) => [...previousItems, ...feedItems]);
    }

    const totalPages = Math.ceil(totalRecord / itemsPerPage);
    setHasMore(currentPage < totalPages);
  }, [currentPage, feedItems, totalRecord]);

  const { ref: bottomRef, inView } = useInView({
    threshold: 0,
    rootMargin: "0px 0px 25% 0px",
  });

  useEffect(() => {
    if (inView && !isFetching && hasMore) {
      setCurrentPage((previousPage) => previousPage + 1);
    }
  }, [hasMore, inView, isFetching]);

  return {
    allData,
    bottomRef,
    cardId,
    currentPage,
    filters,
    hasMore,
    isFetching,
    isInitialLoading: isLoading && currentPage === 1,
    setCurrentPage,
  };
}
